// Features: M004-F0709, M004-F0712. Tests: T-M004-0196, T-M004-0197, T-M004-0203.
//
// The transaction-id chip (Core ML001-D014 section 5e.16.3; design M004-F0712 1.2). Exactly one of two modes:
//   1. copy-only: no TxHistoryProvider mounted, no `orgId`, or an own-account surface (`copyOnly`). One button; click copies TX-xxxxxxxx.
//   2. open + copy: a provider is mounted and the organisation is known. The chip opens the history drawer; a SEPARATE small
//      button copies. There is NO client permission gate on opening (history read = billing.view + membership; a viewer without it
//      gets Core's own 403 sentence in the drawer, never a dead chip).
// The id shown is the TXN (TXN-<ORG4>-<CODE11>) whenever the payload carries one (`txId` = the payload's tx_id, `txnId` = its txn_id);
// the legacy TX-xxxxxxxx only when no TXN is available (displayTxId). Never a placeholder.
// No usable id: nothing (a list row), or, on a drawer header that passes `expected`, one muted sentence.
import React, { useEffect, useRef, useState } from 'react';
import { displayTxId, txIdFromHandOffId, wellFormedTxId, wellFormedTxnId } from '../utils/transactionId';
import { useTxHistory } from './txHistoryContext';
import './TxChip.css';

export const TX_NONE_YET = 'No transaction number yet.';
const COPIED_MS = 1500;

export default function TxChip({ handOffId, orgId, customerUserId, expected = false, copyOnly = false, txId: wireTxId, txnId = null }) {
  const history = useTxHistory();
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const txId = displayTxId({ txnId, txId: wireTxId, handOffId });
  // A legacy wire `TX-` that disagrees with the TX- derived from the hand_off_id is never shown: one developer warning. (A TXN
  // shown instead is the answer and needs no warning.)
  const derived = txIdFromHandOffId(handOffId);
  const wire = wellFormedTxId(wireTxId);
  const mismatch = Boolean(txId === derived && derived && wire && !wellFormedTxnId(wire) && wire !== derived);
  useEffect(() => {
    if (mismatch) console.warn(`TxChip: wire tx_id ${wire} differs from the id derived from hand_off_id (${derived}); showing ${derived}.`);
  }, [mismatch, wire, derived]);

  if (!txId) return expected ? <p className="tx-none-yet bpx-muted" data-testid="tx-none-yet">{TX_NONE_YET}</p> : null;

  // Copying is a convenience: a missing or refusing clipboard changes nothing the viewer can see.
  const copy = () => {
    try {
      const pending = globalThis.navigator?.clipboard?.writeText?.(txId);
      if (pending && typeof pending.then === 'function') {
        const markCopied = () => { setCopied(true); if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => setCopied(false), COPIED_MS); };
        pending.then(markCopied, () => {});
        if (typeof pending.catch === 'function') pending.catch(() => {});
      }
    } catch { /* clipboard unavailable */ }
  };

  const canOpen = !copyOnly && Boolean(orgId) && typeof history?.openTransaction === 'function';
  if (!canOpen) {
    return <button type="button" className="tx-chip" data-testid="tx-chip" title="Transaction id. Click to copy." aria-label={`Transaction id ${txId}. Press to copy.`} onClick={copy}>{txId}</button>;
  }
  return (
    <span className="tx-chip-group">
      <button
        type="button"
        className="tx-chip"
        data-testid="tx-chip"
        title={`Transaction history for ${txId}`}
        aria-label={`Open the history of transaction ${txId}`}
        onClick={() => history.openTransaction({ key: txId, orgId, customerUserId, handOffId })}
      >{txId}</button>
      <button type="button" className="tx-chip-copy" data-testid="tx-chip-copy" aria-label={`Copy ${txId}`} title={`Copy ${txId}`} onClick={copy}>Copy</button>
      {copied ? <span className="tx-chip-copied" role="status" data-testid="tx-chip-copied">Copied</span> : null}
    </span>
  );
}
